"use client";

import { useState } from 'react';

import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import { CalendarIcon, EnvelopeIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

interface FormData {
  email: string;
  website: string;
  description: string;
}

export default function ContactPage() {
  const calendlyUrl = process.env.NEXT_PUBLIC_CALENDLY_URL;
  const [formData, setFormData] = useState<FormData>({
    email: '',
    website: '',
    description: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          website: formData.website,
          description: formData.description,
          message: formData.description,
          source: 'simax-contact-page',
          deploymentId: 'AKfycbwcBk29VPDv_s3Gz-GSyJoXX-Ylfy_3Gu6BX76QlJew-st6hVm6091VvlMLd8zuHrV4oQ',
        }),
      });

      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result?.error || `Request failed with status ${response.status}`);
      }

      setSubmitStatus('success');
      setFormData({ email: '', website: '', description: '' });
    } catch (error) {
      console.error('Contact form submission failed:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <main className="min-h-screen bg-cream">
      <Header />

      {/* Hero Section */}
      <section className="bg-navy pt-24 sm:pt-28 md:pt-32 pb-12 sm:pb-16 md:pb-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-left md:text-center">
          <p className="text-gold text-[10px] sm:text-xs font-bold tracking-[0.2em] uppercase mb-3 sm:mb-4">
            GET STARTED
          </p>
          <h1 className="font-serif text-[28px] sm:text-4xl md:text-5xl lg:text-6xl text-white mb-4 sm:mb-6 leading-tight">
            Let&apos;s Discuss Your{' '}
            <span className="italic">Data Challenges</span>
          </h1>
          <p className="text-white/90 text-[15px] sm:text-base md:text-xl max-w-3xl md:mx-auto leading-relaxed">
            30 minutes to understand your data challenges and map a clear path forward. No sales pressure. Just clarity.
          </p>
        </div>
      </section>

      {/* Two-Column Layout */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
            
            {/* Left Column - Schedule a Call */}
            <div className="bg-white rounded-lg shadow-lg p-5 sm:p-6 md:p-8 lg:p-10">
              <div className="flex items-center gap-3 mb-4 sm:mb-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
                  <CalendarIcon className="w-5 h-5 sm:w-6 sm:h-6 text-gold" />
                </div>
                <h2 className="font-serif text-2xl sm:text-3xl text-navy">Schedule a Call</h2>
              </div>
              
              <p className="text-charcoal/80 text-sm sm:text-base mb-6 sm:mb-8 leading-relaxed">
                Pick a time that works for you. We&apos;ll send a calendar invite with video conference details.
              </p>

              {/* Calendly Embed */}
              {calendlyUrl ? (
                <div className="mb-6 sm:mb-8 rounded-lg overflow-hidden border border-gray-200 bg-white">
                  <iframe
                    title="Schedule a call with Simax Systems"
                    src={calendlyUrl}
                    className="w-full h-[500px] sm:h-[620px] md:h-[700px]"
                    loading="lazy"
                  />
                </div>
              ) : (
                <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-6 sm:p-8 md:p-12 mb-6 sm:mb-8 flex flex-col items-center justify-center min-h-[280px] sm:min-h-[360px] md:min-h-[400px]">
                  <CalendarIcon className="w-12 h-12 sm:w-16 sm:h-16 text-gold mb-3 sm:mb-4" />
                  <p className="text-charcoal font-medium text-center text-sm sm:text-base mb-2">
                    Add NEXT_PUBLIC_CALENDLY_URL in .env to enable scheduling.
                  </p>
                  <p className="text-xs sm:text-sm text-charcoal/60 text-center">
                    Example: https://calendly.com/your-username/30min
                  </p>
                </div>
              )}

              {/* Benefits List */}
              <div className="space-y-3 sm:space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 sm:w-6 sm:h-6 text-gold flex-shrink-0 mt-0.5" />
                  <p className="text-charcoal text-sm sm:text-base">30-minute video call</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 sm:w-6 sm:h-6 text-gold flex-shrink-0 mt-0.5" />
                  <p className="text-charcoal text-sm sm:text-base">No commitment required</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 sm:w-6 sm:h-6 text-gold flex-shrink-0 mt-0.5" />
                  <p className="text-charcoal text-sm sm:text-base">Receive diagnostic summary after call</p>
                </div>
              </div>
            </div>

            {/* Right Column - Contact Form */}
            <div className="bg-white rounded-lg shadow-lg p-5 sm:p-6 md:p-8 lg:p-10">
              <div className="flex items-center gap-3 mb-4 sm:mb-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-navy/10 flex items-center justify-center flex-shrink-0">
                  <EnvelopeIcon className="w-5 h-5 sm:w-6 sm:h-6 text-navy" />
                </div>
                <h2 className="font-serif text-2xl sm:text-3xl text-navy">Send a Message</h2>
              </div>
              
              <p className="text-charcoal/80 text-sm sm:text-base mb-6 sm:mb-8 leading-relaxed">
                Prefer email? Fill out the form below and we&apos;ll respond within 24 business hours.
              </p>

              {/* Contact Form */}
              <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-navy mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@company.com"
                    className="w-full px-4 py-3 min-h-[48px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent transition-all outline-none text-base"
                  />
                </div>

                <div>
                  <label htmlFor="website" className="block text-sm font-medium text-navy mb-2">
                    Company Website (use full URL e.g: https://www.company.com)
                  </label>
                  <input
                    type="url"
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="https://company.com"
                    className="w-full px-4 py-3 min-h-[48px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent transition-all outline-none text-base"
                  />
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-navy mb-2">
                    Brief Description of Your Data Challenge (Optional)
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    rows={5}
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Tell us about your current data situation..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-transparent transition-all outline-none resize-none text-base min-h-[120px]"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full min-h-[48px] bg-gold hover:bg-gold/90 text-navy font-semibold py-4 px-6 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 active:scale-[0.98]"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  <span className="text-lg">→</span>
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center gap-2">
                    <CheckCircleIcon className="w-5 h-5 flex-shrink-0" />
                    <p className="text-sm">Message sent successfully! We&apos;ll respond within 24 hours.</p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
                    <p className="text-sm">Could not send your message right now. Please try again.</p>
                  </div>
                )}
              </form>

              {/* Privacy Note */}
              <div className="mt-6 sm:mt-8 pt-5 sm:pt-6 border-t border-gray-200">
                <div className="flex items-start gap-3">
                  <CheckCircleIcon className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-navy mb-1">Your Privacy Matters</p>
                    <p className="text-xs sm:text-sm text-charcoal/70">
                      All communications are confidential and protected by NDA
                    </p>
                  </div>
                </div>
              </div>

              {/* Direct Contact Information */}
              <div className="mt-6 sm:mt-8 pt-5 sm:pt-6 border-t border-gray-200 space-y-3 sm:space-y-4">
                <h3 className="font-semibold text-navy text-sm sm:text-base mb-3 sm:mb-4">Direct Contact</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-3 min-w-0">
                    <EnvelopeIcon className="w-5 h-5 text-gold flex-shrink-0" />
                    <a href="mailto:contact@simaxsystems.com" className="text-charcoal hover:text-gold transition-colors break-all">
                      contact@simaxsystems.com
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-gold flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <a href="tel:+917893066401" className="text-charcoal hover:text-gold transition-colors">
                      +91-78930 66401
                    </a>
                  </div>
                </div>
                <p className="text-xs text-charcoal/60 mt-4">
                  We respond to all inquiries within 24 business hours
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}